import { useState, useEffect, useCallback } from 'react'
import { ChevronDown, ChevronRight, Database, Layers, X, AlertTriangle, RefreshCw, Loader2 } from 'lucide-react'
import { getReferenceableProjects, getProjectReferences, refreshReference } from '@/api/workbench'
import type { ReferenceableProject, ProjectReference } from '@/types'

interface SelectedRef {
  folder_name: string
  owner_user_id: string
}

interface ReferenceProjectPickerProps {
  /** Current project folder name (for loading existing references) */
  projectId?: string
  /** Controlled selected references */
  value: SelectedRef[]
  /** Called when selection changes */
  onChange: (refs: SelectedRef[]) => void
  /** Disable interaction */
  disabled?: boolean
}

export default function ReferenceProjectPicker({
  projectId,
  value,
  onChange,
  disabled = false,
}: ReferenceProjectPickerProps) {
  const [expanded, setExpanded] = useState(false)
  const [projects, setProjects] = useState<ReferenceableProject[]>([])
  const [staleness, setStaleness] = useState<ProjectReference[]>([])
  const [loading, setLoading] = useState(false)
  const [refreshingRef, setRefreshingRef] = useState<string | null>(null)

  // Load available projects when expanded
  useEffect(() => {
    if (!expanded) return
    setLoading(true)
    getReferenceableProjects()
      .then(setProjects)
      .catch(() => setProjects([]))
      .finally(() => setLoading(false))
  }, [expanded])

  // Load staleness info for existing project
  useEffect(() => {
    if (!projectId || projectId === 'Draft Application') return
    getProjectReferences(projectId)
      .then(setStaleness)
      .catch(() => setStaleness([]))
  }, [projectId])

  const isSelected = useCallback(
    (p: ReferenceableProject) =>
      value.some(
        (r) => r.folder_name === p.folder_name && r.owner_user_id === p.owner_user_id
      ),
    [value]
  )

  const toggleProject = useCallback(
    (p: ReferenceableProject) => {
      if (disabled) return
      if (isSelected(p)) {
        onChange(
          value.filter(
            (r) => !(r.folder_name === p.folder_name && r.owner_user_id === p.owner_user_id)
          )
        )
      } else {
        onChange([...value, { folder_name: p.folder_name, owner_user_id: p.owner_user_id }])
      }
    },
    [value, onChange, isSelected, disabled]
  )

  const handleRefresh = useCallback(
    async (ref: ProjectReference) => {
      if (!projectId) return
      const key = `${ref.folder_name}-${ref.owner_user_id}`
      setRefreshingRef(key)
      try {
        await refreshReference(projectId, ref.folder_name, ref.owner_user_id)
        // Reload staleness
        const updated = await getProjectReferences(projectId)
        setStaleness(updated)
      } catch {
        // ignore
      } finally {
        setRefreshingRef(null)
      }
    },
    [projectId]
  )

  const isStale = useCallback(
    (folderName: string, ownerId: string) =>
      staleness.some(
        (s) => s.folder_name === folderName && s.owner_user_id === ownerId && s.is_stale
      ),
    [staleness]
  )

  const selectedCount = value.length

  return (
    <div style={{
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
      background: 'var(--color-bg-secondary)',
    }}>
      {/* Header / toggle */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        disabled={disabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          width: '100%',
          padding: '0.625rem 0.875rem',
          background: 'none',
          border: 'none',
          color: 'var(--color-text)',
          fontSize: '0.875rem',
          fontWeight: 500,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          textAlign: 'left',
        }}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Layers size={14} style={{ color: 'var(--color-accent)' }} />
        Reference Existing Apps
        {selectedCount > 0 && (
          <span style={{
            marginLeft: 'auto',
            fontSize: '0.8125rem',
            background: 'var(--color-accent)',
            color: '#fff',
            padding: '1px 6px',
            borderRadius: 'var(--radius-full)',
          }}>
            {selectedCount}
          </span>
        )}
      </button>

      {/* Selected chips (always visible when there are selections) */}
      {!expanded && selectedCount > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.375rem',
          padding: '0 0.875rem 0.625rem',
        }}>
          {value.map((ref) => {
            const proj = projects.find(
              (p) => p.folder_name === ref.folder_name && p.owner_user_id === ref.owner_user_id
            )
            const stale = isStale(ref.folder_name, ref.owner_user_id)
            return (
              <span
                key={`${ref.folder_name}-${ref.owner_user_id}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  padding: '2px 8px',
                  fontSize: '0.8125rem',
                  background: stale ? 'rgba(230, 167, 0, 0.12)' : 'rgba(0, 153, 116, 0.1)',
                  color: stale ? '#e6a700' : 'var(--color-accent)',
                  borderRadius: 'var(--radius-full)',
                  border: `1px solid ${stale ? 'rgba(230, 167, 0, 0.3)' : 'rgba(0, 153, 116, 0.2)'}`,
                }}
              >
                {stale && <AlertTriangle size={10} />}
                {proj?.project_name || ref.folder_name}
                {!disabled && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onChange(
                        value.filter(
                          (r) =>
                            !(r.folder_name === ref.folder_name && r.owner_user_id === ref.owner_user_id)
                        )
                      )
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      color: 'inherit',
                      display: 'flex',
                    }}
                  >
                    <X size={10} />
                  </button>
                )}
              </span>
            )
          })}
        </div>
      )}

      {/* Expanded project list */}
      {expanded && (
        <div style={{
          borderTop: '1px solid var(--color-border)',
          maxHeight: 240,
          overflow: 'auto',
        }}>
          {loading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1.5rem',
              gap: '0.5rem',
              color: 'var(--color-text-secondary)',
              fontSize: '0.875rem',
            }}>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              Loading projects...
            </div>
          ) : projects.length === 0 ? (
            <div style={{
              padding: '1.5rem',
              textAlign: 'center',
              color: 'var(--color-text-secondary)',
              fontSize: '0.875rem',
            }}>
              No referenceable projects found
            </div>
          ) : (
            projects.map((p) => {
              const selected = isSelected(p)
              const stale = isStale(p.folder_name, p.owner_user_id)
              const staleRef = staleness.find(
                (s) => s.folder_name === p.folder_name && s.owner_user_id === p.owner_user_id
              )
              const refKey = `${p.folder_name}-${p.owner_user_id}`
              return (
                <div
                  key={refKey}
                  onClick={() => toggleProject(p)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.625rem',
                    padding: '0.5rem 0.875rem',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    background: selected ? 'rgba(0, 153, 116, 0.06)' : 'transparent',
                    borderBottom: '1px solid var(--color-border)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    readOnly
                    disabled={disabled}
                    style={{ flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                      {p.project_name}
                    </div>
                    <div style={{
                      fontSize: '0.8125rem',
                      color: 'var(--color-text-secondary)',
                      display: 'flex',
                      gap: '0.75rem',
                      marginTop: 1,
                    }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                        <Layers size={10} /> {p.object_count}
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                        <Database size={10} /> {p.table_count}
                      </span>
                      <span>{p.owner_name}</span>
                    </div>
                  </div>
                  {stale && staleRef && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRefresh(staleRef)
                      }}
                      disabled={refreshingRef === refKey}
                      title="Source changed — click to refresh"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#e6a700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        fontSize: '0.8125rem',
                        flexShrink: 0,
                      }}
                    >
                      {refreshingRef === refKey ? (
                        <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <RefreshCw size={12} />
                      )}
                    </button>
                  )}
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
